import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { format } from "date-fns";
import { DayOffRequest, DayOffRequestStatus } from "@/types/staff-schedule";
import { useReviewDayOffRequest } from "@/hooks/useStaffSchedule";
import { getUserFriendlyErrorMessage } from "@/utils/httpErrors";

interface ReviewDayOffDialogProps {
  request: DayOffRequest | null;
  staffName: string;
  onClose: () => void;
}

export const ReviewDayOffDialog: React.FC<ReviewDayOffDialogProps> = ({
  request,
  staffName,
  onClose,
}) => {
  const { t } = useTranslation();
  const [reviewNote, setReviewNote] = useState("");
  const { mutate: reviewRequest, isPending } = useReviewDayOffRequest();

  // Reset note when switching to a different request
  useEffect(() => {
    setReviewNote("");
  }, [request?.id]);

  const dates = request
    ? `${format(new Date(request.startDate), "MMM d")} – ${format(new Date(request.endDate), "MMM d, yyyy")}`
    : "";

  const handleReview = (
    status: DayOffRequestStatus.APPROVED | DayOffRequestStatus.REJECTED,
  ) => {
    if (!request) return;
    reviewRequest(
      { id: request.id, data: { status, reviewNote: reviewNote || undefined } },
      {
        onSuccess: () => {
          toast.success(t("staffSchedule.requests.reviewSuccess"));
          setReviewNote("");
          onClose();
        },
        onError: (error) => {
          toast.error(
            getUserFriendlyErrorMessage(
              error,
              t("staffSchedule.requests.reviewError"),
            ),
          );
        },
      },
    );
  };

  return (
    <Dialog
      open={!!request}
      onOpenChange={(open) => {
        if (!open) {
          setReviewNote("");
          onClose();
        }
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t("staffSchedule.requests.review")}</DialogTitle>
          <DialogDescription>
            {staffName} — {dates}
            {request?.reason && (
              <>
                <br />
                {t("staffSchedule.requests.reason")}: {request.reason}
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="reviewNote">
            {t("staffSchedule.requests.reviewNote")}
          </Label>
          <Textarea
            id="reviewNote"
            value={reviewNote}
            onChange={(e) => setReviewNote(e.target.value)}
            placeholder={t("staffSchedule.requests.reviewNotePlaceholder")}
            rows={3}
          />
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="destructive"
            onClick={() => handleReview(DayOffRequestStatus.REJECTED)}
            disabled={isPending}
          >
            {t("staffSchedule.requests.reject")}
          </Button>
          <Button
            variant="new_default"
            onClick={() => handleReview(DayOffRequestStatus.APPROVED)}
            disabled={isPending}
          >
            {t("staffSchedule.requests.approve")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
